import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import {
    FaClipboardList,
    FaFileInvoiceDollar,
    FaBoxOpen,
    FaWallet,
    FaUserCircle,
    FaFileAlt
} from 'react-icons/fa';
import AccountStatement from '../components/AccountStatement';
import InvoiceGenerator from '../components/InvoiceGenerator';
import StatementGenerator from '../components/StatementGenerator';
import { useNavigate } from 'react-router-dom';
import '../styles/ClientDashboard.css';

const ClientRowDocumentDropdown = ({ order, clientId, clientName }) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="position-relative d-inline-block text-start">
            <button
                type="button"
                className="btn btn-sm btn-outline-primary fw-semibold d-flex align-items-center gap-1 dropdown-toggle"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                }}
                style={{ borderRadius: '8px', fontSize: '13px', padding: '5px 12px' }}
            >
                <FaFileAlt size={13} /> Documents
            </button>
            {open && (
                <>
                    <div
                        style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 999 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(false);
                        }}
                    />
                    <div
                        className="dropdown-menu show shadow-lg border-0 p-1"
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: '4px',
                            zIndex: 1000,
                            minWidth: '200px',
                            borderRadius: '12px',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-1" onClick={() => setOpen(false)}>
                            <InvoiceGenerator
                                order={order}
                                buttonLabel="Facture du cas"
                                showButton={true}
                                isExisting={true}
                                className="btn btn-sm btn-light w-100 text-start d-flex align-items-center gap-2 fw-semibold text-dark py-2 px-3 border-0 rounded-2"
                            />
                        </div>
                        <div className="p-1" onClick={() => setOpen(false)}>
                            <StatementGenerator
                                clientId={clientId}
                                clientName={clientName}
                                buttonLabel="Relevé de compte"
                                className="btn btn-sm btn-light w-100 text-start d-flex align-items-center gap-2 fw-semibold text-dark py-2 px-3 border-0 rounded-2"
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const ClientDashboard = () => {
    const { user, profile, signOut } = useAuth();
    const { orders, getClientAccountingSummary } = useData();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('cases');

    // Filter data for current client (using profile.practitioner_id to ensure exact match with DB)
    const clientOrders = (orders || []).filter(o => o.clientId === profile?.practitioner_id || o.client_id === profile?.practitioner_id);

    // Computed summary
    const userName = profile?.full_name || profile?.last_name || profile?.name || user?.email || 'Client';
    const accountingSummary = profile?.practitioner_id ? getClientAccountingSummary(profile.practitioner_id, userName) : { balance: 0 };

    // Sort orders by date (newest first)
    const sortedOrders = [...clientOrders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (!profile) return (
        <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
            <div className="spinner-border text-primary me-3" role="status"></div>
            <span className="text-muted fw-medium">Chargement de votre espace...</span>
        </div>
    );

    const getGreeting = () => {
        // Check profile first (from profiles table), then user_metadata (from auth)
        const meta = user?.user_metadata || {};

        const firstName = profile.first_name || profile.firstName || meta.first_name || '';
        const lastName = profile.last_name || profile.lastName || meta.last_name || '';
        const fullName = profile.full_name || profile.fullName || meta.full_name || '';

        // Build name display: prefer "Dr. FirstName LastName"
        let nameDisplay = '';
        if (firstName || lastName) {
            nameDisplay = `Dr. ${firstName} ${lastName}`.trim();
        } else if (fullName) {
            nameDisplay = `Dr. ${fullName}`;
        } else {
            nameDisplay = user?.email || 'Client';
        }

        return `Bonjour, ${nameDisplay}`;
    };

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    return (
        <div className="client-dashboard">
            {/* Modern Header */}
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h2>{getGreeting()} 👋</h2>
                    <p>Bienvenue sur votre portail ENOVADENT. Voici votre activité récente.</p>
                </div>
                <div className="header-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <button
                        className="action-button"
                        onClick={() => navigate('/profile')}
                        style={{ background: 'white', color: '#3b82f6' }}
                    >
                        <FaUserCircle /> Mon Profil
                    </button>
                    <button
                        className="logout-button"
                        onClick={handleLogout}
                        style={{
                            background: 'rgba(255,255,255,0.2)',
                            border: '2px solid white',
                            color: 'white',
                            padding: '0.7rem 1.2rem',
                            borderRadius: '50px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        Déconnexion
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-container">
                <div className="row g-4">
                    {/* Card 1: Solde à régler */}
                    <div className="col-md-4">
                        <div className="stat-card variant-purple">
                            <div className="stat-content">
                                <h6>Solde à régler</h6>
                                <div className="stat-value">
                                    {accountingSummary.balance > 0.01 
                                        ? `${accountingSummary.balance.toFixed(2)} €` 
                                        : '0.00 €'}
                                </div>
                                <div className="stat-sub">
                                    {accountingSummary.balance > 0.01 ? 'Dette en attente de règlement' : 'Aucune facture en attente'}
                                </div>
                            </div>
                            <div className="stat-icon">
                                <FaWallet />
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Portefeuille / Cagnotte (visible when client has a credit balance) */}
                    {accountingSummary.balance < -0.01 && (
                        <div className="col-md-4">
                            <div className="stat-card variant-green animate__animated animate__bounceIn">
                                <div className="stat-content">
                                    <h6>Portefeuille / Cagnotte</h6>
                                    <div className="stat-value">
                                        + {Math.abs(accountingSummary.balance).toFixed(2)} €
                                    </div>
                                    <div className="stat-sub">Crédit disponible pour vos prochains travaux</div>
                                </div>
                                <div className="stat-icon">
                                    <FaWallet />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Card 3: Travaux en cours */}
                    <div className="col-md-4">
                        <div className="stat-card variant-blue">
                            <div className="stat-content">
                                <h6>Travaux en cours</h6>
                                <div className="stat-value">
                                    {clientOrders.filter(o => !['facturé', 'facture'].includes((o.status || '').toLowerCase())).length}
                                </div>
                                <div className="stat-sub">En production au laboratoire</div>
                            </div>
                            <div className="stat-icon">
                                <FaBoxOpen />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dashboard Content */}
            <div className="dashboard-content">
                {/* Modern Tabs */}
                <div className="custom-tabs">
                    <button
                        className={`tab-item ${activeTab === 'cases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('cases')}
                    >
                        <FaClipboardList className="me-2" /> Mes Travaux
                    </button>
                    <button
                        className={`tab-item ${activeTab === 'accounting' ? 'active' : ''}`}
                        onClick={() => setActiveTab('accounting')}
                    >
                        <FaFileInvoiceDollar className="me-2" /> Factures & Paiements
                    </button>
                </div>

                {/* Tab Content */}
                <div className="tab-pane-content">
                    {activeTab === 'cases' && (
                        <div className="content-card">
                            <div className="table-responsive">
                                <table className="table align-middle">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Numéro</th>
                                            <th>Patient</th>
                                            <th>Type de Travail</th>
                                            <th>Statut</th>
                                            <th className="text-end">Documents</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedOrders.length === 0 ? (
                                            <tr>
                                                <td colSpan="6">
                                                    <div className="empty-state">
                                                        <FaBoxOpen className="empty-icon" />
                                                        <p className="empty-text">Aucun travail en cours pour le moment.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedOrders.map(order => {
                                                const s = (order.status || '').toLowerCase();
                                                const isBilled = s === 'facturé' || s === 'facture';
                                                const clientStatus = isBilled ? 'Facturé' : 'Reçu';

                                                return (
                                                    <tr key={order.id}>
                                                        <td className="fw-medium text-muted">
                                                            {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                                        </td>
                                                        <td className="font-monospace text-primary fw-bold">
                                                            {order.caseNumber || order.id.substring(0, 8).toUpperCase()}
                                                        </td>
                                                        <td className="fw-bold text-dark">
                                                            {order.patientName}
                                                        </td>
                                                        <td>
                                                            {order.workType || (order.teethConfig ? 'Prothèse' : 'Divers')}
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge ${isBilled ? 'status-facturé' : 'status-reçu'}`}>
                                                                {clientStatus}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <ClientRowDocumentDropdown
                                                                order={order}
                                                                clientId={profile?.practitioner_id}
                                                                clientName={userName}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'accounting' && (
                        <div className="animate__animated animate__fadeIn">
                            <AccountStatement clientId={profile?.practitioner_id} clientName={userName} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientDashboard;
